import CultureIcon from "@assets/icons/culture.svg?react";
import EmailIcon from "@assets/icons/email.svg?react";
import newsletterBg from "@assets/images/newsletter-bg.png";
import newsletterBgCulture from "@assets/images/newsletter-bg-culture.png";
import ThinkChinaLogo from "@assets/images/tc-logomark-white@2x.svg?react";
import Logomark from "@assets/logomark.png";
import InputBox from "@components/InputBox/InputBox";
import useIsSmallScreen from "@hooks/useIsSmallScreen";
import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import type { NewsletterVariant } from "@src/app/hooks/useNewsletterSignUp";
import { useNewsletterSignUp } from "@src/app/hooks/useNewsletterSignUp";
import clsx from "clsx";
import type { ReactNode } from "react";
import { useLocation } from "react-router";

interface NewsletterSignUpProps {
  variant?: NewsletterVariant;
  className?: string;
  withIcon?: boolean;
}

const VARIANT_CONFIG = {
  default: {
    bg: newsletterBgCulture,
    title: "Get the ThinkChina Weekly Newsletter",
    description: "Insights on China, right in your mailbox. Sign up now.",
    containerClassName: "w-full",
    contentClassName: "lg:w-[640px]",
    titleClassName: "lg:text-header-2 lg:whitespace-nowrap",
  },
  regional: {
    bg: newsletterBgCulture,
    title: "Get the Regional Series Newsletter",
    description:
      "Sign up for ThinkChina Weekly Newsletter and receive our Regional Series straight in your inbox!",
    containerClassName: "w-full",
    contentClassName: "lg:w-[640px]",
    titleClassName: "lg:text-header-2",
  },
  culture: {
    bg: newsletterBgCulture,
    title: "ThinkCulture Newsletter",
    description:
      "Your fortnightly dose of Chinese history, culture, and cartoons. Sign up now.",
    containerClassName: "w-full",
    contentClassName: "lg:w-[640px]",
    titleClassName: "lg:text-header-2",
  },
  card: {
    bg: newsletterBgCulture,
    title: "Get the ThinkChina Weekly Newsletter",
    description: "Insights on China, right in your mailbox. Sign up now.",
    containerClassName: "lg:w-full bg-background-default",
    contentClassName: "w-full",
    titleClassName: "text-primary",
  },
  cultureCard: {
    bg: newsletterBg,
    title: "ThinkCulture Newsletter",
    description:
      "Your fortnightly dose of Chinese history, culture, and cartoons. Sign up now.",
    containerClassName: "lg:w-full bg-background-default",
    contentClassName: "w-full",
    titleClassName: "text-primary",
  },
  regionalCard: {
    bg: newsletterBgCulture,
    title: "Get the Regional Series Newsletter",
    description:
      "Sign up for ThinkChina Weekly Newsletter and receive our Regional Series straight in your inbox!",
    containerClassName: "lg:w-full bg-background-default",
    contentClassName: "w-full",
    titleClassName: "text-primary",
  },
};

export default function NewsletterSignUp({
  variant = "default",
  className,
  withIcon = true,
}: NewsletterSignUpProps): ReactNode {
  const location = useLocation();
  const sendDataToGTM = useGTMDispatch();
  const { value, setValue, success, error, onSubmit } =
    useNewsletterSignUp(variant);
  const isSmallScreen = useIsSmallScreen();

  const config = VARIANT_CONFIG[variant];

  //is default view
  const isDefault =
    variant === "default" || variant === "culture" || variant === "regional";
  const newsletterName =
    variant === "cultureCard" || variant === "culture" ? "culture" : "weekly";
  const pageUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `https://www.thinkchina.sg${location.pathname}${location.search}`;

  const trackNewsletterEvent = (action: "focus" | "submit") => {
    sendDataToGTM({
      event: "tcCustomEvent",
      eventCategory: "newsletter",
      eventAction:
        action === "focus"
          ? `focus ${newsletterName}`
          : `submit ${newsletterName}`,
      eventLabel: pageUrl,
    });
  };

  const renderIcon = () => {
    if (!withIcon) return null;

    if (isDefault) {
      return (
        <ThinkChinaLogo className="h-[79.725px] w-12.5 shrink-0 lg:h-25 lg:w-15.75" />
      );
    }

    return (
      <div>
        {variant === "regionalCard" ? (
          <img src={Logomark} alt="ThinkChina" className="h-[100px] w-auto" />
        ) : (
          <div className="border-divider-secondary flex shrink-0 items-center justify-center rounded-full border bg-white p-4">
            {variant === "cultureCard" ? (
              <CultureIcon className="text-primary size-10" />
            ) : (
              <EmailIcon className="text-primary size-10" />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      data-testid="newsletter-signup"
      className={clsx("flex flex-col items-center px-3 py-4", className)}
    >
      <div
        className={clsx(
          "relative flex w-full flex-1 flex-col items-center overflow-hidden",
          config.containerClassName,
        )}
      >
        {/* Background Image */}
        <img
          src={config.bg}
          alt=""
          className="pointer-events-none absolute inset-0 size-full object-cover object-center"
        />

        {/* Content Container */}
        <div
          className={clsx(
            "relative flex flex-1 flex-col items-center px-3 py-8",
            config.contentClassName,
          )}
        >
          <div className="flex w-full items-center gap-3 px-3">
            {renderIcon()}

            <div className="flex flex-1 flex-col items-start gap-1">
              <h2
                className={clsx(
                  "text-header-3 flex w-full items-start font-bold tracking-[-0.02em]",
                  config.titleClassName,
                )}
              >
                {config.title}
              </h2>
              <p className="text-label-1 text-primary flex w-full items-start font-bold tracking-[0.01em]">
                {config.description}
              </p>
            </div>
          </div>

          <div
            className="relative w-full px-3 py-4"
            onFocusCapture={() => {
              trackNewsletterEvent("focus");
            }}
          >
            <InputBox
              variant="newsletter"
              success={success}
              error={error}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
              onSubmit={(email) => {
                trackNewsletterEvent("submit");
                void onSubmit(email);
              }}
              isMobileView={!isDefault || isSmallScreen}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
